import _, { isNil } from "lodash";

import { ConstraintComparison, ConstraintTarget, ConstraintTypes } from "../../../constants/constraints";
import { Precision } from "../../../types";
import { isDateValid } from "../../definition/calendar/dateFunctions";
import dateByAddingSpan from "../../definition/calendar/dateFunctions/dateByAddingSpan";
import { extractDateFromYearString } from "../../definition/calendar/dateFunctions/dateWithString";

const getTargetDateFromItem = (calendar, item, target) => {
  switch (target) {
    case ConstraintTarget.Start:
      return item.startDate;
    case ConstraintTarget.End:
      return item.endDate;
  }
};

const getTargetTimestampFromItem = (item, target) => {
  switch (target) {
    case ConstraintTarget.Start:
      return item.startDate ? item.startDate.timestamp : null;
    case ConstraintTarget.End:
      return item.endDate ? item.endDate.timestamp : null;
  }
};

const getDatePart = (date, precision) => {
  switch (precision) {
    case Precision.Year:
      return date.year;
    case Precision.Month:
      return date.month;
    case Precision.Week:
      return date.weekday;
    case Precision.Day:
      return date.day;
    case Precision.Hour:
      return date.hour;
    case Precision.Minute:
      return date.minute;
    case Precision.Second:
      return date.second;
    default:
      return null;
  }
};

/**
 * The timestamp values could be zero that should check null by isNil()
 * @param {number} value timestamp
 * @param {number} comparisonValue timestamp
 * @param {string} comparison
 */
const doesDateViolateComparison = (value, comparisonValue, comparison) => {
  switch (comparison) {
    case ConstraintComparison.After:
      if (isNil(value) || isNil(comparisonValue)) {
        return true;
      }
      return value < comparisonValue;
    case ConstraintComparison.Before:
      if (isNil(value) || isNil(comparisonValue)) {
        return true;
      }
      return value > comparisonValue;
    case ConstraintComparison.Equal:
      return value != comparisonValue;
    default:
      return true;
  }
};

const isRelativeConstraintViolated = (
  calendar,
  subjectDates,
  objectDates,
  subjectTarget,
  objectTarget,
  comparison,
  offset
) => {
  // if (!subjectTarget) {
  //   return false;
  // }
  const subjectDate = getTargetDateFromItem(calendar, subjectDates, subjectTarget);
  const objectDate = getTargetDateFromItem(calendar, objectDates, objectTarget);
  if (!isDateValid(subjectDate) || !isDateValid(objectDate)) {
    // If either does not have a date, then it is violated
    return true;
  }
  const comparisonDate = offset ? dateByAddingSpan(calendar, objectDate, offset) : objectDate;
  return doesDateViolateComparison(subjectDate.timestamp, comparisonDate.timestamp, comparison);
};

const isDateConstraintViolated = (subjectDates, subjectTarget, comparison, date) => {
  const subjectTs = getTargetTimestampFromItem(subjectDates, subjectTarget);
  return _.isNil(subjectTs) || doesDateViolateComparison(subjectTs, date.timestamp, comparison);
};

const getNumericValueForDatePart = (calendar, partPrecision, value) => {
  if (partPrecision === Precision.Week) {
    // Convert weekday name to numeric day of week value...
    const weekdayIndex = calendar.weekdays.findIndex(d => d.name === value || d.shortName === value);
    if (weekdayIndex >= 0) {
      return weekdayIndex;
    }
  }
  if (partPrecision === Precision.Month) {
    // Convert month name to numeric value...
    const monthIndex = calendar.months.findIndex(m => m.name === value || m.shortName === value);
    if (monthIndex >= 0) {
      return monthIndex + 1;
    }
  }
  // Otherwise, it should already be a number
  return Number(value);
};

const isDatePartConstraintViolated = (calendar, subjectDates, subjectTarget, partPrecision, partValue) => {
  const subjectDate = getTargetDateFromItem(calendar, subjectDates, subjectTarget);
  if (!subjectDate) {
    return true;
  }
  const part = getDatePart(subjectDate, partPrecision);
  // Divide into terms
  const terms = _.map(partValue.split(","), _.trim);
  // Iterate through terms to see if we match any of them!
  const matchFound = _.find(terms, t => {
    // Divide any ranges, and also convert to numeric values.
    const range = t.split("-");
    if (range.length == 1) {
      // Only a single value, so it either matches or it doesn't
      if (partPrecision === Precision.Year) {
        const date = extractDateFromYearString(calendar, t);
        if (date) {
          return part === date.year && subjectDate.era === date.era;
        }
      }
      const comparison = getNumericValueForDatePart(calendar, partPrecision, t);
      return part === comparison;
    } else {
      // Format x-y, so anything inside the range x-y is valid.
      // Note that we may want to allow the range to wrap... e.g. Fri-Mon, Dec-Feb
      // So if range[0] < range[1], do a normal range.
      if (partPrecision === Precision.Year) {
        const date1 = extractDateFromYearString(calendar, range[0]);
        const date2 = extractDateFromYearString(calendar, range[1]);
        if (date1 && date2) {
          if (date1.timestamp < date2.timestamp) {
            // Normal range... so it matches if it is above first value and below second
            return subjectDate.timestamp >= date1.timestamp && subjectDate.timestamp <= date2.timestamp;
          } else {
            // Range is reversed... so it is true if it is above first or below second
            return subjectDate.timestamp >= date1.timestamp || subjectDate.timestamp >= date2.timestamp;
          }
        }
      }
      const comparison1 = getNumericValueForDatePart(calendar, partPrecision, range[0]);
      const comparison2 = getNumericValueForDatePart(calendar, partPrecision, range[1]);
      if (comparison1 < comparison2) {
        // Normal range... so it matches if it is above first value and below second
        return part >= comparison1 && part <= comparison2;
      } else {
        // Range is reversed... so it is true if it is above first or below second
        return part >= comparison1 || part >= comparison2;
      }
    }
  });
  // If we found a match, return false as it is not violated.
  return matchFound ? false : true;
};

// Given a subject, object, dependencyType, and offset...
// Work out if we currently meet the conditions of this dependency or not!
export const isConstraintViolated = (c, calendar, itemDatesMap) => {
  const subjectDates = itemDatesMap.get(c.subject);

  switch (c.constraintType) {
    case ConstraintTypes.Relative: {
      const objectDates = itemDatesMap.get(c.object);
      return isRelativeConstraintViolated(
        calendar,
        subjectDates,
        objectDates,
        c.subjectTarget,
        c.objectTarget,
        c.comparison,
        c.offset
      );
    }
    case ConstraintTypes.Date: {
      return isDateConstraintViolated(subjectDates, c.subjectTarget, c.comparison, c.date);
    }
    case ConstraintTypes.DatePart: {
      return isDatePartConstraintViolated(calendar, subjectDates, c.subjectTarget, c.partPrecision, c.partValue);
    }
    default:
      return false;
  }
};
